import React, {Component, ReactNode} from 'react'
import {PlainLinkStyledButton} from '../../../styledComponents/Button'
import styled, {css} from 'styled-components'
import {FontSizeInPixel, HTMLBaseFontSizeInPixel} from '../../../styledComponents/Constants'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import { Collapse } from '@mui/material';

interface Props {
    contents: string,
    initialOpen: boolean
    showScroll: boolean
    continueButtonLabel?: string
    fontSizeInPixel?: number
    lineHeightInPixel?: number
    maxDisplayLines?: number
}

interface State {
    open: boolean
    needContinueButton: boolean,
}

export default class CollapseHTMLDisplay extends Component<Props, State> {

    private myRef: React.RefObject<HTMLDivElement>
    private collapsedHeightLimit: number
    private lineHeightInPixel: number
    private fontSizeInPixel: number
    private clientHeight: number

    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            open: false,
            needContinueButton: false,
        }

        this.fontSizeInPixel = this.props.fontSizeInPixel || FontSizeInPixel.Body
        this.lineHeightInPixel = this.props.lineHeightInPixel || FontSizeInPixel.Big

        this.collapsedHeightLimit = (this.props.maxDisplayLines || 5) * (this.lineHeightInPixel)
        this.clientHeight = this.collapsedHeightLimit

        this.myRef = React.createRef()
        this.handleResize = this.handleResize.bind(this)
    }

    updateContinueButtonVisibility(): void {
        if (!this.myRef || !this.myRef.current) {
            return
        }
        this.clientHeight = this.myRef.current.clientHeight
        this.setState({
            needContinueButton: (this.myRef.current.clientHeight > this.collapsedHeightLimit),
        })
    }

    componentDidMount(): void {
        this.updateContinueButtonVisibility()
        window.addEventListener('resize', this.handleResize)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.contents === this.props.contents) {
            return
        }
        this.updateContinueButtonVisibility()
    }

    componentWillUnmount(): void{
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize(): void {
        this.updateContinueButtonVisibility()
    }

    render(): ReactNode {
        if (!this.props.contents || this.props.contents.length === 0) {
            return null
        }
        const height: number = (this.clientHeight > this.collapsedHeightLimit)
            ? this.collapsedHeightLimit
            : this.clientHeight

        return <>
            <StyledCollapse
                collapsedSize={`${height / HTMLBaseFontSizeInPixel}rem`}
                in={this.props.initialOpen || this.state.open}
            >
                <HTMLDiv
                    ref={this.myRef}
                    fontSizeInPixel={this.fontSizeInPixel}
                    lineHeightInPixel={this.lineHeightInPixel}
                    maxDisplayLines={this.props.maxDisplayLines || 13}
                    showScroll={this.props.showScroll}
                    dangerouslySetInnerHTML={{__html: this.props.contents || ''}}
                />
            </StyledCollapse>
            {
                (!(this.props.initialOpen || this.state.open) && this.state.needContinueButton) &&
                <>
                    <BlockSpacer height={'5px'}/>
                    <ContinueLink
                        fontSizeInPixel={this.fontSizeInPixel}
                        lineHeightInPixel={this.lineHeightInPixel}
                        onClick={() => this.setState({open: true})}>
                        {this.props.continueButtonLabel || '続きを見る'}
                    </ContinueLink>
                </>
            }
        </>
    }
}

interface HTMLDivProps {
    showScroll: boolean
    fontSizeInPixel: number
    lineHeightInPixel: number
    maxDisplayLines: number
}

const HTMLDiv = styled.div<HTMLDivProps>`
  font-size: ${props => (props.fontSizeInPixel) / HTMLBaseFontSizeInPixel}rem;
  line-height: ${props => (props.lineHeightInPixel) / HTMLBaseFontSizeInPixel}rem;
  ${props => {
    if (props.showScroll) {
        return css`
            overflow-y: scroll;
            max-height: ${(props.lineHeightInPixel) / HTMLBaseFontSizeInPixel * (props.maxDisplayLines)}rem;
          `
    }
}};
  word-break: break-word;
`

const StyledCollapse = styled(Collapse)`
  &.MuiCollapse-root {
    width: 100%;
  }
`

const ContinueLink = styled(PlainLinkStyledButton)<{ fontSizeInPixel: number, lineHeightInPixel: number }>`
  font-size: ${props => props.fontSizeInPixel / HTMLBaseFontSizeInPixel}rem;
  line-height: ${props => props.lineHeightInPixel / HTMLBaseFontSizeInPixel}rem;
`
